import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
    static targets = ["item", "bullet", "wrapper"]
    static values = {
        activeClass: String,
        autoscroll: Boolean,
        autoscrollInterval: Number, default: 3,
        sheetId: { type: String, default: "home-editor-menu-sheet" }
    }

    connect() {
      this.currentIndex = 0
      this.items = this.itemTargets
      if (this.autoscrollValue) {
        this.startAutoscroll()
      }

      this.intersectionObserver = new IntersectionObserver(this.handleIntersectionObserver.bind(this), {
        root: this.wrapperTarget,
        threshold: 0.75
      })

      this.items.forEach(item => this.intersectionObserver.observe(item))
    }

    disconnect() {
      clearInterval(this.interval)
      this.intersectionObserver.disconnect()
    }

    startAutoscroll() {
      const interval = (this.autoscrollIntervalValue) * 1000
      this.interval = setInterval(() => this.next(), interval)
    }

    next(event) {
      event?.preventDefault()
      this.resetAutoscroll()

      const isWrappingAround = this.currentIndex === this.items.length - 1
      this.currentIndex = (this.currentIndex + 1) % this.items.length

      this.scrollToCurrentItem({
        smoothWrap: isWrappingAround,
        wrapDirection: isWrappingAround ? "forward" : null
      })
    }

    previous(event) {
      event?.preventDefault()
      this.resetAutoscroll()

      const isWrappingAround = this.currentIndex === 0
      this.currentIndex =
        (this.currentIndex - 1 + this.items.length) % this.items.length

      this.scrollToCurrentItem({
        smoothWrap: isWrappingAround,
        wrapDirection: isWrappingAround ? "backward" : null
      })
    }

    goTo(event) {
      event?.preventDefault()
      this.resetAutoscroll()

      const index = parseInt(event.params.index, 10)
      if (isNaN(index)) return

      this.currentIndex = index
      this.scrollToCurrentItem()
    }

    isWrapperInView() {
      const rect = this.wrapperTarget.getBoundingClientRect()
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight

      return rect.top >= 0 && rect.top < viewportHeight
    }

    resetAutoscroll() {
      if (this.autoscrollValue) {
        clearInterval(this.interval)
        this.startAutoscroll()
      }
    }

    async scrollToCurrentItem({ smoothWrap = false, wrapDirection = null } = {}) {
      const wrapper = this.wrapperTarget

      if (!this.isWrapperInView()) return

      const currentItem = this.items[this.currentIndex]
      if (!currentItem) return

      const itemRect = currentItem.getBoundingClientRect()
      const wrapperRect = wrapper.getBoundingClientRect()
      const scrollLeft = wrapper.scrollLeft
      const itemLeftRelativeToWrapper = itemRect.left - wrapperRect.left
      const targetScrollPosition = scrollLeft + itemLeftRelativeToWrapper

      if (smoothWrap && wrapDirection) {
        const clone = (
          wrapDirection === "forward"
            ? this.items[0].cloneNode(true)
            : this.items[this.items.length - 1].cloneNode(true)
        )

        if (wrapDirection === "forward") {
          wrapper.appendChild(clone)
        } else {
          wrapper.prepend(clone)
          const cloneWidth = clone.offsetWidth
          wrapper.scrollLeft += cloneWidth // account for DOM shift
        }

        // Scroll to clone using scrollTo
        wrapper.scrollTo({
          left: wrapDirection === "forward"
            ? wrapper.scrollWidth - wrapper.clientWidth
            : 0,
          behavior: "smooth"
        })

        await new Promise(resolve => setTimeout(resolve, 400)) // wait for animation to finish

        // Jump back to real item and clean up
        wrapper.style.scrollBehavior = "auto"
        wrapper.scrollLeft = targetScrollPosition
        wrapper.removeChild(clone)
        wrapper.style.scrollBehavior = ""
      } else {
        // Regular scroll using scrollTo
        wrapper.scrollTo({
          left: targetScrollPosition,
          behavior: "smooth"
        })
      }
    }

    handleIntersectionObserver(entries) {
        entries.forEach((entry) => {
          const elem = entry.target
          const intersectedItemIndex = this.items.findIndex(item => item === elem)

          if (entry.isIntersecting) {
            if (entry.intersectionRatio >= 0.75) {
              this.currentIndex = intersectedItemIndex
              this.bulletTargets[intersectedItemIndex].classList.add(this.activeClassValue)
            }
          } else {
            this.bulletTargets[intersectedItemIndex].classList.remove(this.activeClassValue)
          }
        })
    }
}
